import styled, { css } from 'styled-components';
import { pxToRem } from 'modules/v2/foundation/spacing';
import { themeGet } from 'modules/v2/foundation/theme';
export const Input = styled.input `
  border: none;
  background-color: transparent;
  flex: 1 1 100%;
  font-size: ${themeGet('fontSize.medium')};
  line-height: ${themeGet('lineHeight.default')};
  color: ${themeGet('colors.text.300')};

  &[type='search']::-webkit-search-decoration,
  &[type='search']::-webkit-search-cancel-button,
  &[type='search']::-webkit-search-results-button,
  &[type='search']::-webkit-search-results-decoration {
    display: none;
  }

  &:focus-visible {
    outline: none;
  }

  &:disabled {
    color: ${themeGet('colors.disabled.200')};
  }
`;
export const Adornment = styled.div `
  flex: 1 1 auto;
`;
const outlineInputDisabledStyles = css `
  box-shadow: none;
  border: ${pxToRem(1)} dashed ${themeGet('colors.disabled.200')};
`;
const outlineInputDefaultStyles = css `
  box-shadow: 0 0 0 ${pxToRem(1)} ${themeGet('colors.border.100')}; /* firefox fallback until :has selector is supported */

  &:has(input:not(:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(1)} ${themeGet('colors.border.100')};
  }

  &:hover:not(:has(input:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${themeGet('colors.border.100')};
  }

  &:has(input:focus-visible:not(:disabled)) {
    background-color: ${themeGet('colors.background.50')};
    box-shadow: 0 0 0 ${pxToRem(2)} ${themeGet('colors.border.300')};

    ${Adornment} svg {
      color: ${themeGet('colors.text.100')};
    }
  }

  &:has(input:active:not(:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${themeGet('colors.border.300')};
  }

  &:has(input:disabled) {
    ${outlineInputDisabledStyles};
  }
`;
const outlineInputInvalidStyles = css `
  box-shadow: 0 0 0 ${pxToRem(1)} ${themeGet('colors.text.danger')};

  &:hover:not(:has(input:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${themeGet('colors.text.danger')};
  }

  &:has(input:focus-visible:not(:disabled)) {
    background-color: ${themeGet('colors.background.50')};
    box-shadow: 0 0 0 ${pxToRem(2)} ${themeGet('colors.text.danger')};

    ${Adornment} svg {
      color: ${themeGet('colors.text.100')};
    }
  }

  &:has(input:active:not(:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${themeGet('colors.text.danger')};
  }

  &:has(input:disabled) {
    ${outlineInputDisabledStyles};
  }
`;
const lineInputDisabledStyles = css `
  border-bottom: ${pxToRem(1)} dashed ${themeGet('colors.disabled.200')};
  box-shadow: none;
`;
const lineInputDefaultStyles = css `
  background-color: transparent;
  box-shadow: 0 ${pxToRem(1)} 0 0 ${themeGet('colors.border.200')}; /* firefox fallback until :has selector is supported */

  &:has(input:not(:disabled)) {
    box-shadow: 0 ${pxToRem(1)} 0 0 ${themeGet('colors.border.200')};
  }

  &:hover:not(:has(input:disabled)) {
    box-shadow: 0 ${pxToRem(2)} 0 0 ${themeGet('colors.border.200')};
  }

  &:has(input:focus-visible:not(:disabled)) {
    box-shadow: 0 ${pxToRem(2)} 0 0 ${themeGet('colors.border.300')};

    ${Adornment} svg {
      color: ${themeGet('colors.text.100')};
    }
  }

  &:has(input:active:not(:disabled)) {
    box-shadow: 0 ${pxToRem(2)} 0 0 ${themeGet('colors.border.300')};
  }

  &:has(input:disabled) {
    ${lineInputDisabledStyles};
  }
`;
const lineInputInvalidStyles = css `
  box-shadow: 0 ${pxToRem(1)} 0 0 ${themeGet('colors.text.danger')};

  &:hover:not(:has(input:disabled)) {
    box-shadow: 0 ${pxToRem(2)} 0 0 ${themeGet('colors.text.danger')};
  }

  &:has(input:focus-visible:not(:disabled)) {
    box-shadow: 0 ${pxToRem(2)} 0 0 ${themeGet('colors.text.danger')};

    ${Adornment} svg {
      color: ${themeGet('colors.text.100')};
    }
  }

  &:has(input:active:not(:disabled)) {
    box-shadow: 0 ${pxToRem(2)} 0 0 ${themeGet('colors.text.danger')};
  }

  &:has(input:disabled) {
    ${lineInputDisabledStyles};
  }
`;
const outlineInputStyles = css `
  padding: ${themeGet('spacing.md')} ${themeGet('spacing.lg')};
  border-radius: ${themeGet('borderRadius.md')};
  background-color: ${themeGet('colors.background.100')};
  ${(props) => !props.$invalid && outlineInputDefaultStyles};
  ${(props) => props.$invalid && outlineInputInvalidStyles};
`;
const lineInputStyles = css `
  padding: ${themeGet('spacing.md')} 0;
  ${(props) => !props.$invalid && lineInputDefaultStyles};
  ${(props) => props.$invalid && lineInputInvalidStyles};
`;
export const InputContainer = styled.div `
  box-sizing: border-box;
  position: relative;
  width: ${(props) => props.$stretch && '100%;'};
  display: flex;
  align-items: center;
  justify-content: space-between;
  column-gap: ${themeGet('spacing.lg')};
  ${(props) => props.$variant === 'outline' && outlineInputStyles};
  ${(props) => props.$variant === 'line' && lineInputStyles};
  margin: ${pxToRem(2)}; /* prevent box-shadow border from being hidden/covered in case of container not having any padding */
  transition: box-shadow 0.15s;
`;
